// src/components/GoLiveModal.js
// ============================================================================
// GO LIVE (header button + modal) - design S7.6, P14
//
// Default export GoLiveButton: the gold "Go Live" pill in the Header's right
// section, shown to every visitor (logged out included). Named export
// GoLiveModal: the three-choice modal it opens on the web.
//
//   Inside the CritCast app   -> window.critcashDesktop.broadcast.open()
//                                expands the Broadcast panel; no modal. An
//                                app without the method opens the Hub.
//   Phone or tablet           -> modal: G-08 and choice 3 only
//   Desktop browser           -> modal: 1 Open CritCast, 2 Download CritCast,
//                                3 Stream with your own software
//   A remembered handoff      -> the modal opens in "Opening CritCast..."
//     (critcastSeenAt)          and tries the app at once
//
// Every branching rule and string lives in src/utils/critcastLaunch.js (the
// harness drives it there). The app launch must start inside the press
// handler: the hidden iframe needs the click's user gesture.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { isDesktopApp } from '../utils/platform';
import { isMobileWeb } from '../utils/googleAuth';
import { DESKTOP_RELEASE } from '../constants/desktopRelease';
import {
  G,
  ROUTE_DOWNLOAD,
  ROUTE_HUB,
  goLiveTarget,
  goLiveModalModel,
  downloadPressTarget,
  isCompactHeader,
  launch,
  recordLaunchResult,
  getStorage,
  readSeenAt,
  ownSoftwareTarget,
  openBroadcastPanel,
  createInfoLoader,
} from '../utils/critcastLaunch';

const COLORS = {
  GOLD: '#FCD000',
  INK: '#0d0d0d',
  CARD: '#111',
  BORDER: '#222',
  TEXT: '#EDEDED',
  MUTED: '#888',
  CYAN: '#00BFFF',
  BACKDROP: 'rgba(0, 0, 0, 0.85)',
};

// One getDesktopClientInfo call per page session, shared with the /download
// page (DesktopDownloadScreen imports it). The callable is public.
export const loadDesktopClientInfo = createInfoLoader(async () => {
  const { data } = await httpsCallable(functions, 'getDesktopClientInfo')({});
  return data || null;
});

// Fire-and-forget download count; a failure never blocks the download.
export function recordWindowsDownload() {
  try {
    httpsCallable(functions, 'recordDesktopDownload')({ platform: 'windows' }).catch(() => {});
  } catch (e) {
    // stats are not critical
  }
}

function safeNavigate(navigation, name, params) {
  try {
    if (navigation && typeof navigation.navigate === 'function') navigation.navigate(name, params);
  } catch (e) {
    // a missing route never breaks the header
  }
}

// ============================================================================
// HEADER BUTTON
// ============================================================================

export default function GoLiveButton({ navigation, isLoggedIn }) {
  const inDesktop = isDesktopApp();
  const { width } = useWindowDimensions();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('choices');
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      attemptRef.current += 1;
    };
  }, []);

  const compact = isCompactHeader(width);

  // Choice 1, and the remembered-handoff press. Runs inside a press handler.
  const startLaunch = () => {
    attemptRef.current += 1;
    const attempt = attemptRef.current;
    setView('launching');
    launch().then((result) => {
      const verdict = recordLaunchResult(result, getStorage());
      if (!mountedRef.current || attempt !== attemptRef.current) return;
      if (verdict.close) {
        setOpen(false);
        setView('choices');
      } else {
        setView(verdict.view);
      }
    });
  };

  const close = () => {
    attemptRef.current += 1;
    setOpen(false);
    setView('choices');
  };

  const onPress = () => {
    const target = goLiveTarget({
      inDesktop,
      mobile: isMobileWeb(),
      loggedIn: !!isLoggedIn,
      seenAt: readSeenAt(getStorage()),
    });
    if (target === 'panel') {
      openBroadcastPanel().then((outcome) => {
        if (outcome !== 'opened') safeNavigate(navigation, ROUTE_HUB);
      });
      return;
    }
    if (target === 'mobile') {
      setView('mobile');
      setOpen(true);
      return;
    }
    if (target === 'launch') {
      setOpen(true);
      startLaunch();
      return;
    }
    setView('choices');
    setOpen(true);
  };

  return (
    <>
      <TouchableOpacity
        style={[styles.pill, compact && styles.pillCompact]}
        onPress={onPress}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={compact ? G['G-12'] : G['G-01']}
      >
        <MaterialCommunityIcons name="broadcast" size={16} color={COLORS.INK} />
        {!compact && <Text style={styles.pillText}>{G['G-01']}</Text>}
      </TouchableOpacity>
      {!inDesktop && (
        <GoLiveModal
          visible={open}
          view={view}
          onClose={close}
          onOpenApp={startLaunch}
          navigation={navigation}
          isLoggedIn={!!isLoggedIn}
        />
      )}
    </>
  );
}

// ============================================================================
// MODAL
// ============================================================================

export function GoLiveModal({ visible, view, onClose, onOpenApp, navigation, isLoggedIn }) {
  const [info, setInfo] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (!visible) return;
    loadDesktopClientInfo()
      .then((data) => { if (mountedRef.current) setInfo(data); })
      .catch(() => { if (mountedRef.current) setInfo(null); });
  }, [visible]);

  const model = goLiveModalModel({ view, info, release: DESKTOP_RELEASE });

  const goDownloadPage = () => {
    onClose && onClose();
    safeNavigate(navigation, ROUTE_DOWNLOAD);
  };

  const onDownload = async () => {
    if (downloadBusy) return;
    setDownloadBusy(true);
    let latest = info;
    try {
      latest = await loadDesktopClientInfo();
    } catch (e) {
      latest = null;
    }
    // `info` is what the modal showed at the press: G-10 appears only once a
    // published build is known, so a press made before that goes to the
    // Download page and its early build box instead of straight to the exe.
    const target = downloadPressTarget({ shownInfo: info, latestInfo: latest, release: DESKTOP_RELEASE });
    if (mountedRef.current) setDownloadBusy(false);
    if (target.kind === 'url') {
      recordWindowsDownload();
      try {
        window.location.assign(target.url);
      } catch (e) {
        goDownloadPage();
        return;
      }
      onClose && onClose();
      return;
    }
    goDownloadPage();
  };

  const onOwnSoftware = () => {
    const target = ownSoftwareTarget({ signedIn: !!isLoggedIn });
    onClose && onClose();
    safeNavigate(navigation, target.route, target.params);
  };

  if (!visible) return null;

  const showChoice = (n) => model.choices.includes(n);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={(e) => e && e.stopPropagation && e.stopPropagation()}>
          <View style={styles.iconWrap}>
            <MaterialCommunityIcons name="broadcast" size={34} color={COLORS.GOLD} />
          </View>
          <Text style={styles.title} accessibilityRole="header">{model.title}</Text>

          {model.busy && (
            <View style={styles.launching}>
              <ActivityIndicator size="small" color={COLORS.GOLD} />
              <Text style={styles.launchingText}>{model.note}</Text>
            </View>
          )}
          {!model.busy && model.note && <Text style={styles.note}>{model.note}</Text>}

          {showChoice(1) && (
            <View style={styles.choice}>
              <TouchableOpacity
                style={styles.primaryBtn}
                onPress={onOpenApp}
                activeOpacity={0.85}
                accessibilityRole="button"
              >
                <Text style={styles.primaryBtnText}>{G['G-03']}</Text>
              </TouchableOpacity>
              <Text style={styles.detail}>{G['G-03a']}</Text>
            </View>
          )}

          {showChoice(2) && (
            <View style={styles.choice}>
              {model.unsignedNote && <Text style={styles.unsignedNote}>{model.unsignedNote}</Text>}
              <TouchableOpacity
                style={[styles.secondaryBtn, downloadBusy && styles.btnBusy]}
                onPress={onDownload}
                disabled={downloadBusy}
                activeOpacity={0.85}
                accessibilityRole="button"
              >
                {downloadBusy
                  ? <ActivityIndicator size="small" color={COLORS.TEXT} />
                  : <Text style={styles.secondaryBtnText}>{G['G-04']}</Text>}
              </TouchableOpacity>
              <Text style={styles.detail}>{model.downloadDetail}</Text>
              <TouchableOpacity onPress={goDownloadPage} accessibilityRole="link">
                <Text style={styles.link}>{G['G-11']}</Text>
              </TouchableOpacity>
            </View>
          )}

          {showChoice(3) && (
            <View style={styles.choice}>
              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={onOwnSoftware}
                activeOpacity={0.85}
                accessibilityRole="button"
              >
                <Text style={styles.secondaryBtnText}>{G['G-05']}</Text>
              </TouchableOpacity>
              <Text style={styles.detail}>{G['G-05a']}</Text>
            </View>
          )}

          <TouchableOpacity style={styles.closeBtn} onPress={onClose} accessibilityRole="button">
            <Text style={styles.closeBtnText}>{model.closeLabel}</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  // Header pill (S7.6 "Look")
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 32,
    borderRadius: 16,
    paddingVertical: 0,
    paddingHorizontal: 14,
    backgroundColor: COLORS.GOLD,
  },
  pillCompact: {
    gap: 0,
  },
  pillText: {
    color: COLORS.INK,
    fontWeight: '700',
    fontSize: 13,
  },

  // Modal
  backdrop: {
    flex: 1,
    backgroundColor: COLORS.BACKDROP,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: COLORS.CARD,
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    alignItems: 'stretch',
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  iconWrap: {
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 21,
    fontWeight: '800',
    color: COLORS.TEXT,
    textAlign: 'center',
    marginBottom: 14,
  },
  launching: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 18,
  },
  launchingText: {
    color: COLORS.TEXT,
    fontSize: 15,
    fontWeight: '600',
  },
  note: {
    color: COLORS.TEXT,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 14,
  },
  choice: {
    marginBottom: 16,
  },
  primaryBtn: {
    backgroundColor: COLORS.GOLD,
    borderRadius: 10,
    paddingVertical: 13,
    alignItems: 'center',
  },
  primaryBtnText: {
    color: COLORS.INK,
    fontWeight: '800',
    fontSize: 16,
  },
  secondaryBtn: {
    backgroundColor: '#1a1a1a',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#333',
    paddingVertical: 12,
    alignItems: 'center',
    minHeight: 46,
    justifyContent: 'center',
  },
  secondaryBtnText: {
    color: COLORS.TEXT,
    fontWeight: '700',
    fontSize: 15,
  },
  btnBusy: {
    opacity: 0.7,
  },
  detail: {
    color: COLORS.MUTED,
    fontSize: 12,
    lineHeight: 17,
    marginTop: 6,
    textAlign: 'center',
  },
  unsignedNote: {
    color: '#c9c9c9',
    fontSize: 12,
    lineHeight: 17,
    backgroundColor: '#17150a',
    borderWidth: 1,
    borderColor: '#3a3310',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
  },
  link: {
    color: COLORS.CYAN,
    fontSize: 12,
    marginTop: 6,
    textAlign: 'center',
  },
  closeBtn: {
    paddingVertical: 10,
    alignItems: 'center',
  },
  closeBtnText: {
    color: '#777',
    fontSize: 14,
  },
});
